

import { Building2, CreditCard, CheckCircle2, Wrench, Plus } from "lucide-react";
import { formatCurrency, formatCurrencyFull } from "@/lib/data";
import Badge from "../../components/Badge";
import { EventVendor, EventService } from "./types";

interface VendorListProps {
  vendors: EventVendor[] | undefined;
  services?: EventService[] | undefined;
  onAddPayment?: () => void;
  onPayVendor?: (vendorId: number) => void;
  onAddVendor?: () => void;
  onAddService?: () => void;
}

export default function VendorList({ 
  vendors, 
  services, 
  onAddPayment, 
  onPayVendor,
  onAddVendor,
  onAddService 
}: VendorListProps) {
  const hasVendors = vendors && vendors.length > 0;
  const hasServices = services && services.length > 0;

  if (!hasVendors && !hasServices) {
    return (
      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
        <div className="px-4 py-3 bg-stone-50 border-b border-stone-200 flex items-center justify-between">
          <h3 className="font-bold text-stone-800 text-sm flex items-center gap-2">
            <Building2 className="w-4 h-4 text-blue-500" />
            Assigned Vendors &amp; Services
          </h3>
          <div className="flex gap-2">
            {onAddVendor && (
              <button
                onClick={onAddVendor}
                className="text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-1"
              >
                <Plus className="w-3 h-3" />
                Add Vendor
              </button>
            )}
            {onAddService && (
              <button
                onClick={onAddService}
                className="text-xs px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium transition-colors flex items-center gap-1"
              >
                <Plus className="w-3 h-3" />
                Add Service
              </button>
            )}
          </div>
        </div>
        <div className="p-8 text-center">
          <Building2 className="w-12 h-12 text-stone-200 mx-auto mb-3" />
          <p className="text-stone-400 text-sm mb-4">No vendors or services assigned yet</p>
          <div className="flex gap-2 justify-center">
            {onAddVendor && (
              <button
                onClick={onAddVendor}
                className="text-xs px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-1"
              >
                <Plus className="w-3 h-3" />
                Add Vendor
              </button>
            )}
            {onAddService && (
              <button
                onClick={onAddService}
                className="text-xs px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium transition-colors flex items-center gap-1"
              >
                <Plus className="w-3 h-3" />
                Add Service
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Vendor totals
  const totalVendorBilled = (vendors || []).reduce((sum, v) => sum + (v.total_amount || 0), 0);
  const totalPaid = (vendors || []).reduce((sum, v) => sum + (v.paid_amount || 0), 0);
  const totalBalance = totalVendorBilled - totalPaid;

  // Service totals
  const totalServiceBilled = (services || []).reduce(
    (sum, s) => sum + (s.quantity || 1) * (s.unit_price || 0),
    0
  );

  const grandTotal = totalVendorBilled + totalServiceBilled;

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-stone-50 border-b border-stone-200 flex items-center justify-between">
        <h3 className="font-bold text-stone-800 text-sm flex items-center gap-2">
          <Building2 className="w-4 h-4 text-blue-500" />
          Assigned Vendors &amp; Services
          <span className="text-xs text-stone-400 font-normal">
            ({(vendors?.length || 0) + (services?.length || 0)} items)
          </span>
        </h3>
        <div className="flex gap-2">
          {onAddVendor && (
            <button
              onClick={onAddVendor}
              className="text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-1"
            >
              <Plus className="w-3 h-3" />
              Add Vendor
            </button>
          )}
          {onAddService && (
            <button
              onClick={onAddService}
              className="text-xs px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium transition-colors flex items-center gap-1"
            >
              <Plus className="w-3 h-3" />
              Add Service
            </button>
          )}
          {totalBalance > 0 && onAddPayment && (
            <button
              onClick={onAddPayment}
              className="text-xs px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors flex items-center gap-1"
            >
              <CreditCard className="w-3 h-3" />
              Pay Vendor
            </button>
          )}
        </div>
      </div>

      {/* Summary bar */}
      {(hasVendors || hasServices) && (
        <div className="px-4 py-3 bg-stone-50 border-b border-stone-200">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
            {hasVendors && (
              <>
                <span className="text-stone-600">
                  Vendors: <span className="font-semibold text-stone-800">{formatCurrencyFull(totalVendorBilled)}</span>
                </span>
                <span className="text-emerald-600">
                  Paid: <span className="font-semibold">{formatCurrencyFull(totalPaid)}</span>
                </span>
                {totalBalance > 0 ? (
                  <span className="text-amber-600 font-semibold">
                    Balance: {formatCurrencyFull(totalBalance)}
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-emerald-600">
                    <CheckCircle2 className="w-3 h-3" />
                    All Paid
                  </span>
                )}
              </>
            )}
            {hasServices && (
              <span className="text-violet-600">
                Services: <span className="font-semibold">{formatCurrencyFull(totalServiceBilled)}</span>
              </span>
            )}
          </div>
          {/* Overall payment progress bar */}
          {hasVendors && totalVendorBilled > 0 && (
            <div className="mt-2">
              <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min((totalPaid / totalVendorBilled) * 100, 100)}%` }}
                />
              </div>
              <div className="flex justify-between mt-0.5">
                <span className="text-[10px] text-stone-400">
                  {Math.round((totalPaid / totalVendorBilled) * 100)}% paid
                </span>
                <span className="text-[10px] text-stone-400">
                  Total: {formatCurrencyFull(totalVendorBilled)}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="divide-y divide-stone-100">
        {/* Event Vendors */}
        {(vendors || []).map((vendor) => {
          const paidAmount = vendor.paid_amount || 0;
          const totalAmount = vendor.total_amount || 0;
          const remaining = totalAmount - paidAmount;
          const isPaid = remaining <= 0;
          const paidPct = totalAmount > 0 ? Math.round((paidAmount / totalAmount) * 100) : 0;

          return (
            <div key={`vendor-${vendor.id}`} className="p-3 sm:p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-start gap-2 min-w-0">
                  <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Building2 className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-stone-800 text-sm">{vendor.vendor?.name || "Unknown Vendor"}</p>
                    <p className="text-xs text-stone-500">
                      {vendor.description || vendor.vendor?.vendor_type?.name || "Vendor Service"}
                    </p>
                    {(vendor.quantity ?? 0) > 0 && (
                      <p className="text-xs text-stone-400 mt-0.5">
                        Qty: {vendor.quantity} {vendor.unit || "unit"}
                        {vendor.cost_price ? ` @ ${formatCurrencyFull(vendor.cost_price)}` : ""}
                      </p>
                    )}
                    {vendor.notes && (
                      <p className="text-xs text-stone-400 mt-0.5 italic">{vendor.notes}</p>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                  <p className="font-semibold text-stone-800 text-sm">{formatCurrencyFull(totalAmount)}</p>
                  <Badge label={vendor.status} status={vendor.status} />
                </div>
              </div>

              {/* Payment progress */}
              {totalAmount > 0 && (
                <div className="ml-9 mb-2">
                  <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${isPaid ? "bg-emerald-400" : "bg-blue-400"}`}
                      style={{ width: `${paidPct}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-stone-400 mt-0.5">{paidPct}% paid</p>
                </div>
              )}

              <div className="flex items-center justify-between text-xs ml-9">
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  <span className="text-emerald-600">Paid: <span className="font-semibold">{formatCurrencyFull(paidAmount)}</span></span>
                  <span className={remaining > 0 ? "text-amber-600 font-semibold" : "text-emerald-600"}>
                    Balance: <span className="font-semibold">{formatCurrencyFull(remaining)}</span>
                  </span>
                </div>
                {!isPaid && onPayVendor && (
                  <button
                    onClick={() => onPayVendor(vendor.id)}
                    className="flex items-center gap-1 px-2.5 py-1 text-white bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors font-medium text-xs"
                    title="Give Payment"
                  >
                    <CreditCard className="w-3 h-3" />
                    Pay
                  </button>
                )}
                {isPaid && (
                  <span className="flex items-center gap-1 text-emerald-600">
                    <CheckCircle2 className="w-3 h-3" />
                    Paid
                  </span>
                )}
              </div>
            </div>
          );
        })}

        {/* Event Services */}
        {(services || []).map((service) => {
          const lineTotal = (service.quantity || 1) * (service.unit_price || 0);
          const costTotal = (service.quantity || 1) * (service.cost_price || 0);
          const label = service.description || service.service?.name || "Service";

          return (
            <div key={`service-${service.id}`} className="p-3 sm:p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-2 min-w-0">
                  <div className="w-7 h-7 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Wrench className="w-3.5 h-3.5 text-violet-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-stone-800 text-sm">{label}</p>
                    <p className="text-xs text-stone-400 mt-0.5">
                      Qty: {service.quantity} @ {formatCurrencyFull(service.unit_price)}
                    </p>
                    {service.cost_price != null && service.cost_price > 0 && (
                      <p className="text-xs text-stone-400">
                        Cost: {formatCurrencyFull(costTotal)}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                  <p className="font-semibold text-stone-800 text-sm">{formatCurrencyFull(lineTotal)}</p>
                  <span className="text-xs text-violet-600 font-medium">Service</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
